import {
  ConditionType,
  DATE_BUCKET_OPTIONS,
  DATE_TRUNC_FUNCTION_NAME,
  DateBucketValueType,
  DateTruncExpression,
  DescriptorType,
  DimTypes,
  EXPLODE_FUNCTION_NAME,
  ExpressionTypes,
  FieldTypesMap,
  FormatterType,
  RepeatedDimType,
} from '@fabric-ui/components';
import { AllTypeWidgets, ExplodeExpression, PivotTableRequest } from '../types';

const COLLECT_DISTINCT = 'COLLECT_DISTINCT';
const COLLECT_DISTINCT_FLAT = 'COLLECT_DISTINCT_FLAT';

export function isDateTruncExpression(name: string): boolean;
export function isDateTruncExpression(expression: ExpressionTypes): expression is DateTruncExpression;
export function isDateTruncExpression(arg: string | ExpressionTypes): boolean {
  if (typeof arg === 'string') {
    return arg === DATE_TRUNC_FUNCTION_NAME;
  }
  if (typeof arg === 'object' && arg !== null && 'function' in arg && arg.function) {
    return arg.function.name === DATE_TRUNC_FUNCTION_NAME;
  }
  return false;
}
export function isExplodeExpression(name: string): boolean;
export function isExplodeExpression(expression: ExpressionTypes): expression is ExplodeExpression;
export function isExplodeExpression(arg: string | ExpressionTypes): boolean {
  if (typeof arg === 'string') {
    return arg === EXPLODE_FUNCTION_NAME;
  }
  if (typeof arg === 'object' && arg !== null && 'function' in arg && arg.function) {
    return arg.function.name === EXPLODE_FUNCTION_NAME;
  }
  return false;
}

export const getDateTruncExpression = (fieldName: string, bucket: DateBucketValueType): DateTruncExpression => ({
  function: {
    name: DATE_TRUNC_FUNCTION_NAME,
    args: [
      {
        symbol: {
          ref: fieldName,
        },
      },
      {
        stringLiteral: {
          value: bucket,
        },
      },
    ],
  },
});

export const getDateBucketFromExpression = (expression: DateTruncExpression) => expression.function.args[1].stringLiteral.value;

export const getSimplifiedDateTruncExpression = (fieldName: string, bucket: string) => {
  const alias = `${fieldName}_${bucket}`;

  return {
    [fieldName]: {
      name: fieldName,
      function: DATE_TRUNC_FUNCTION_NAME,
      bucket,
      alias,
    },
  };
};

export const getSimplifiedExplodeExpression = (dimName: string) => ({
  [dimName]: {
    name: dimName,
    function: EXPLODE_FUNCTION_NAME,
    alias: `${dimName}_${EXPLODE_FUNCTION_NAME}`,
  },
});

export const getSimplifiedExpression = (dimName: string, expression: ExpressionTypes) => {
  if (isDateTruncExpression(expression)) {
    return getSimplifiedDateTruncExpression(dimName, getDateBucketFromExpression(expression));
  }

  if (isExplodeExpression(expression)) {
    return getSimplifiedExplodeExpression(dimName);
  }

  return undefined;
};

export const getFieldTypeByExpression = (expression: ExpressionTypes): FormatterType | undefined => {
  const functionName = getExpressionFunctionName(expression);
  if (isDateTruncExpression(functionName)) {
    return FormatterType.historicDate;
  }

  if (isExplodeExpression(functionName)) {
    return FormatterType.string;
  }

  return undefined;
};

export const checkIsNoneDateBucket = (
  bucket:
    | typeof DATE_BUCKET_OPTIONS.day.value
    | typeof DATE_BUCKET_OPTIONS.week.value
    | typeof DATE_BUCKET_OPTIONS.month.value
    | typeof DATE_BUCKET_OPTIONS.year.value
    | typeof DATE_BUCKET_OPTIONS.none.value
) => bucket === DATE_BUCKET_OPTIONS.none.value;

export const getTitleWithDateTrunc = (dimTitle, bucket: keyof typeof DATE_BUCKET_OPTIONS) =>
  checkIsNoneDateBucket(bucket) ? dimTitle : `${dimTitle} (${DATE_BUCKET_OPTIONS[bucket]?.title})`;

export const getExpressionFunctionName = (expression: ExpressionTypes): string => expression.function.name;

export const getFieldTitleByExpression = ({
  expression,
  displayName,
}: {
  expression: ExpressionTypes;
  displayName: string | number | null | undefined;
}): string => {
  if (isDateTruncExpression(expression)) {
    return getTitleWithDateTrunc(displayName, getDateBucketFromExpression(expression));
  }

  if (isExplodeExpression(expression)) {
    return `${displayName} (Expanded)`;
  }

  return 'no title';
};

export const getExpressionArgs = (expression: ExpressionTypes): unknown[] | undefined => expression.function.args;
export const getCollectExpression = (dimName: string, isRepeated: boolean) => ({
  function: {
    name: 'ARRAY_SLICE',
    args: [
      {
        function: {
          name: isRepeated ? COLLECT_DISTINCT_FLAT : COLLECT_DISTINCT,
          args: [
            {
              symbol: {
                ref: dimName,
              },
            },
          ],
        },
      },
      {
        number: {
          value: 0,
        },
      },
      {
        number: {
          value: 25,
        },
      },
    ],
  },
});

export const getExplodeExpression = (dimName: string): ExplodeExpression => ({
  function: {
    name: EXPLODE_FUNCTION_NAME,
    args: [
      {
        symbol: {
          ref: dimName,
        },
      },
    ],
  },
});

export const getExpression = (fieldName: string, functionName: string, bucket: DateBucketValueType) => {
  if (isDateTruncExpression(functionName)) {
    return getDateTruncExpression(fieldName, bucket);
  }

  if (isExplodeExpression(functionName)) {
    return getExplodeExpression(fieldName);
  }

  return undefined;
};

export const removeExpression = ({ newWidget, fieldName }: { newWidget: AllTypeWidgets; fieldName: string }) => {
  const currentRequest = newWidget.requests[0];
  const { select, groupBy, sorting } = currentRequest;
  const { sortingCol } = currentRequest as PivotTableRequest;
  const currentDims = select.dims;

  const dimToUpdate = currentDims.find(dim => dim.name === fieldName);
  const originalAlias = dimToUpdate?.alias;

  // Update the dimension in select.dims: remove alias and expression properties.
  const newSelectDims = currentDims.map(dim => {
    if (dim.name === fieldName) {
      const { alias, expression, ...rest } = dim; // Exclude alias and expression
      return rest;
    }
    return dim;
  });

  // Construct the new select object with updated dims
  const newSelect = {
    ...select,
    dims: newSelectDims,
  };

  // Update groupBy, sorting, and sortingCol if an originalAlias was present.
  // Replace the originalAlias with fieldName in these arrays.
  const finalGroupBy = originalAlias ? groupBy?.map(groupDim => (groupDim === originalAlias ? fieldName : groupDim)) : groupBy;

  const finalSortBy = originalAlias
    ? sorting.map(sortItem => {
        if (sortItem.name === originalAlias) {
          return { ...sortItem, name: fieldName };
        }
        return sortItem;
      })
    : sorting;

  const finalSortingCol = originalAlias
    ? sortingCol?.map(sortColItem => {
        if (sortColItem.name === originalAlias) {
          return { ...sortColItem, name: fieldName };
        }
        return sortColItem;
      })
    : sortingCol;

  // Construct the final widget with all updated properties
  const finalWidget = {
    ...newWidget,
    requests: [
      {
        ...currentRequest,
        select: newSelect,
        groupBy: finalGroupBy,
        sorting: finalSortBy,
        ...(finalSortingCol !== undefined && { sortingCol: finalSortingCol }),
      },
    ],
  };

  return finalWidget as AllTypeWidgets;
};

export const addExpression = ({
  newWidget,
  fieldName,
  alias,
  expression,
}: {
  newWidget: AllTypeWidgets;
  fieldName: string;
  alias: string;
  expression: ExpressionTypes;
}) => {
  const currentRequest = newWidget.requests[0];
  const { select, groupBy, sorting } = currentRequest;
  const currentDims = select.dims;

  const dimToUpdate = currentDims.find(dim => dim.name === fieldName);
  const oldAlias = dimToUpdate?.alias; // Capture old alias if it exists

  // If no dimension matches fieldName, return widget unchanged
  if (!dimToUpdate) {
    return newWidget;
  }

  // Update the dimension in select.dims: retain original name, add alias and expression
  const newSelectDims = currentDims.map(dim => {
    if (dim.name === fieldName) {
      return { ...dim, alias, expression };
    }
    return dim;
  });

  // Construct the new select object with updated dims
  const newSelect = {
    ...select,
    dims: newSelectDims,
  };

  // Update groupBy: replace fieldName OR oldAlias with new alias if present
  const newGroupBy = groupBy?.map(groupDim => {
    if (groupDim === fieldName || (oldAlias && groupDim === oldAlias)) {
      return alias;
    }
    return groupDim;
  });

  // Update sorting: replace fieldName OR oldAlias in SortBy.name with new alias if present
  const newSortBy = sorting.map(sortItem => {
    if (sortItem.name === fieldName || (oldAlias && sortItem.name === oldAlias)) {
      return { ...sortItem, name: alias };
    }
    return sortItem;
  });

  // Update sortingCol (if present): replace fieldName OR oldAlias in SortBy.name with new alias if present
  const newSortingCol = (currentRequest as PivotTableRequest).sortingCol?.map(sortColItem => {
    if (sortColItem.name === fieldName || (oldAlias && sortColItem.name === oldAlias)) {
      return { ...sortColItem, name: alias };
    }
    return sortColItem;
  });

  // Construct the final widget with all updated properties
  const finalWidget = {
    ...newWidget,
    requests: [
      {
        ...currentRequest,
        select: newSelect,
        groupBy: newGroupBy,
        sorting: newSortBy,
        ...(newSortingCol !== undefined && { sortingCol: newSortingCol }),
      },
    ],
  };

  return finalWidget as AllTypeWidgets;
};

export const getType = type => {
  if (type.name && type.name.includes('Timestamp')) {
    return DimTypes.Date;
  }

  if (type.fields) {
    return DimTypes.Object;
  }

  if (type.kind) {
    return type.kind === 'TYPE_ENUM' ? DimTypes.String : FieldTypesMap[DescriptorType[type.kind]];
  }

  return undefined;
};

// Some repeated fields hold a raw message value (e.g. Fix's { fix, type }) instead of a plain
// primitive, so filter building, drill-down, and row-selection/highlighting all need to (a) know
// which filter condition to compare the field against on the backend and (b) know how to pull a
// comparable primitive out of the message. Register that logic once per field type here instead
// of special-casing it at every call site — adding support for a new message-typed field going
// forward should only require adding an entry to this list.
export type ArrayFieldAdapter = {
  /** The `underlyingCondition` to pass to `getExpression` when filtering on this field. */
  underlyingCondition: ConditionType;
  /** Extracts the comparable primitive from a raw value of this field (no-op for plain values). */
  extractValue: (value: any) => any;
};

const ARRAY_FIELD_ADAPTERS: { matches: (fieldMap: Record<string, any>, name: string) => boolean; adapter: ArrayFieldAdapter }[] = [
  {
    matches: (fieldMap, name) => !!fieldMap?.[name]?.originalType?.typeName?.endsWith('.Fix'),
    adapter: {
      underlyingCondition: ConditionType.FIX,
      extractValue: value => (value && typeof value === 'object' && 'fix' in value ? value.fix : value),
    },
  },
];

// Returns the adapter for `name` if it needs special message-value handling, otherwise undefined.
export const getArrayFieldAdapter = (fieldMap: Record<string, any>, name: string): ArrayFieldAdapter | undefined =>
  ARRAY_FIELD_ADAPTERS.find(({ matches }) => matches(fieldMap, name))?.adapter;

// Convenience helper for callers that only need value extraction (e.g. row-equality checks).
export const extractArrayFieldValue = (fieldMap: Record<string, any>, name: string, value: any): any =>
  getArrayFieldAdapter(fieldMap, name)?.extractValue(value) ?? value;

export const getDimType = ({ fieldMap, name }) => {
  if (fieldMap[name]) {
    const dim = fieldMap[name];
    const isRepeated = fieldMap[name].repeated;

    const type = getType(dim.originalType);

    if (isRepeated) {
      return RepeatedDimType[type];
    }

    return type;
  }

  return undefined;
};
